import { useCallback, useEffect, useState } from 'react'
import { Box, Button, Input, InputPhone, Text, Title, Toaster } from '@holbonkit/ui'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { type SbpBank, type SelfEmployedPayoutMethod, useSaveSBPB2CDriverBank } from '@/shared/api'
import { useAppNavbar } from '@/shared/hooks/use-app-navbar'
import { useLocalStorage } from '@/shared/hooks/use-local-storage'

import { EMPTY_PAYOUT_METHOD } from '@/entities/smz'

import { editValidate } from '../model/edit-validate'
import { accountLinkValidator } from '../model/validator'

import { ChangeBottomSheet } from './bottom-sheets'
import { Sbp } from './sbp'

import styles from './account-link.module.scss'

const toSelectedBank = (payout: Partial<SelfEmployedPayoutMethod> | undefined): null | SbpBank => {
    if (!payout?.bank_id) {
        return null
    }

    return {
        bic: '',
        icon_url: payout.bank_icon_url ?? null,
        id: payout.bank_id,
        name: payout.bank_name ?? '',
        popularity_score: null,
    }
}

export const AccountLink = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const isEditMode: boolean = location.search.edit_mode === true
    const isWrongSbp: boolean = location.search.wrong_sbp === true

    useAppNavbar(' ', 'Назад', 'onNavbarBackButtonClick()')

    const { mutate: saveSBPB2CDriverBank } = useSaveSBPB2CDriverBank({
        mutation: {
            onSuccess: () => {
                if (isEditMode || isWrongSbp) {
                    handleChange(getValues())
                } else {
                    setAccountLink(getValues())
                    navigate({
                        to: '/withdrawal-smz',
                        search: { sbpType: 'created', from: 'account-link' },
                    })
                }
            },
            onError: () => {
                toast.error('Ошибка при сохранении данных')
            },
        },
    })

    const { getValue: getAccountLink, setValue: setAccountLink } =
        useLocalStorage<SelfEmployedPayoutMethod>(
            'withdrawal-funds-account-link',
            EMPTY_PAYOUT_METHOD,
        )

    const [defaultValues] = useState<SelfEmployedPayoutMethod>(() => {
        const previous = getAccountLink()

        if (!isWrongSbp) {
            return previous
        }

        const next = { ...EMPTY_PAYOUT_METHOD, email: previous.email }

        setAccountLink(next)

        return next
    })

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        formState: { isValid },
        clearErrors,
    } = useForm<SelfEmployedPayoutMethod>({
        defaultValues,
        mode: 'onChange',
    })

    const currentValues = useWatch({ control })
    const isEditValid = editValidate(
        getAccountLink(),
        currentValues as SelfEmployedPayoutMethod,
        isValid,
    )

    const [isChangeMode, setIsChangeMode] = useState(false)

    const handleNavbarBackButtonClick = useCallback(() => {
        navigate({
            to: '/withdrawal-smz',
            search: {
                from: 'account-link',
            },
        })
    }, [navigate])

    useEffect(() => {
        globalThis.window.onNavbarBackButtonClick = handleNavbarBackButtonClick

        return () => {
            delete globalThis.window.onNavbarBackButtonClick
        }
    }, [handleNavbarBackButtonClick])

    const handleChange = (values: SelfEmployedPayoutMethod) => {
        const previous = getAccountLink()

        const sbpUpdated = () => {
            if (isWrongSbp) {
                return 'bank_phone'
            }
            if (previous.bank_id !== values.bank_id) {
                return 'bank'
            }

            if (previous.phone !== values.phone) {
                return 'phone'
            }

            if (previous.email === values.email) {
                return null
            }

            return 'email'
        }

        const updated = sbpUpdated()

        setAccountLink(values)

        navigate({
            to: '/withdrawal-smz',
            search: {
                sbpType: 'updated',
                sbpUpdated: updated,
                sbpUpdatedValue: updated === 'bank' ? values.bank_name : undefined,
                from: 'account-link',
            },
        })
    }

    const renderText = () => {
        if (isEditMode) {
            return (
                <Text className={styles['account-link__item--text']} variant='body-s-14-regular'>
                    Номер не должен быть привязан к чужому счёту СБП
                </Text>
            )
        }

        if (isWrongSbp) {
            return (
                <Text className={styles['account-link__item--text']} variant='body-s-14-regular'>
                    Номер не должен быть привязан к чужому счёту СБП
                </Text>
            )
        }

        return (
            <Text className={styles['account-link__item--text']} variant='body-s-14-regular'>
                Номер должен быть привязан к вашему счёту СБП. Если счёт принадлежит другому
                человеку, добавить СБП не получится
            </Text>
        )
    }

    return (
        <form
            noValidate
            id='account-link-form'
            onSubmit={handleSubmit(() => {
                if (isEditMode || isWrongSbp) {
                    setIsChangeMode(true)
                } else {
                    saveSBPB2CDriverBank({
                        data: {
                            sbp_bank_id: getValues().bank_id,
                            phone: getValues().phone,
                            email: getValues().email ?? undefined,
                        },
                    })
                }
            })}
        >
            <Box className={styles['account-link']}>
                <Title variant='header-24-bold'>
                    {isEditMode || isWrongSbp ? 'Данные счёта СБП' : 'Добавление счёта СБП'}
                </Title>

                <Controller
                    control={control}
                    name='bank_id'
                    rules={accountLinkValidator.bank_id}
                    render={({ field, fieldState }) => (
                        <Sbp
                            editMode={isEditMode}
                            errorText={fieldState.isTouched ? fieldState.error?.message : undefined}
                            selectedBank={toSelectedBank(currentValues)}
                            onSelect={(bank) => {
                                setValue('bank_id', bank.id, {
                                    shouldTouch: true,
                                    shouldValidate: true,
                                })
                                setValue('bank_name', bank.name)
                                setValue('bank_icon_url', bank.icon_url)
                                setValue('method', 'sbp')
                                field.onBlur()
                            }}
                        />
                    )}
                />

                <Box className={styles['account-link__item']}>
                    <Controller
                        control={control}
                        name='phone'
                        rules={accountLinkValidator.phone}
                        render={({ field, fieldState }) => (
                            <InputPhone
                                inputMode='numeric'
                                label='Номер телефона'
                                name={field.name}
                                size='lg'
                                value={field.value}
                                errorText={
                                    fieldState.isTouched ? fieldState.error?.message : undefined
                                }
                                onBlur={field.onBlur}
                                onChange={field.onChange}
                                onFocus={() => {
                                    clearErrors('phone')
                                }}
                            />
                        )}
                    />
                    {renderText()}
                </Box>
                <Box className={styles['account-link__item']}>
                    <Controller
                        control={control}
                        name='email'
                        rules={accountLinkValidator.email}
                        render={({ field, fieldState }) => (
                            <Input
                                inputMode='email'
                                label='Электронная почта'
                                name={field.name}
                                size='lg'
                                value={field.value ?? ''}
                                errorText={
                                    fieldState.isTouched ? fieldState.error?.message : undefined
                                }
                                onBlur={field.onBlur}
                                onChange={field.onChange}
                                onFocus={() => {
                                    clearErrors('email')
                                }}
                            />
                        )}
                    />

                    {!isEditMode && !isWrongSbp && (
                        <Text
                            className={styles['account-link__item--text']}
                            variant='body-s-14-regular'
                        >
                            Будем отправлять на почту чеки о списании сервисного платежа Drivee и
                            НДС раз в месяц
                        </Text>
                    )}
                </Box>
                <Button
                    className={styles['account-link__button']}
                    disabled={isEditMode ? !isEditValid : !isValid}
                    size='lg'
                    type='submit'
                    variant='primary'
                >
                    {isEditMode || isWrongSbp ? 'Сохранить' : 'Добавить'}
                </Button>
            </Box>

            <ChangeBottomSheet
                bank={getValues().bank_name}
                isChangeMode={isChangeMode}
                phone={getValues().phone}
                setChangeMode={() => setIsChangeMode(false)}
                handleChange={() =>
                    saveSBPB2CDriverBank({
                        data: {
                            sbp_bank_id: getValues().bank_id,
                            phone: getValues().phone,
                            email: getValues().email ?? undefined,
                        },
                    })
                }
            />
            <Toaster position='bottom-center' />
        </form>
    )
}
